import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, PieChart, Pie, Legend,
} from 'recharts';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';

const SERVICE_COLORS = [
  '#4285F4', '#34A853', '#FBBC05', '#EA4335',
  '#9b72f5', '#00ACC1', '#FF7043', '#8BC34A',
];

const StatusBadge = ({ enabled }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
    background: enabled ? 'rgba(74,222,128,0.1)' : 'rgba(239,68,68,0.1)',
    color: enabled ? '#4ade80' : '#f87171',
    border: `1px solid ${enabled ? 'rgba(74,222,128,0.25)' : 'rgba(239,68,68,0.25)'}`,
  }}>
    <span style={{ width: 6, height: 6, borderRadius: '50%', background: enabled ? '#4ade80' : '#f87171' }} />
    {enabled ? 'Billing On' : 'Billing Off'}
  </span>
);

const StatCard = ({ icon, label, value, sub, color = '#4285F4' }) => (
  <div style={styles.statCard}>
    <div style={{ ...styles.statIcon, background: color + '18', border: `1px solid ${color}28` }}>{icon}</div>
    <div style={styles.statValue}>{value}</div>
    <div style={styles.statLabel}>{label}</div>
    {sub && <div style={styles.statSub}>{sub}</div>}
  </div>
);

export default function CostOverviewPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = selectedProject ? `?projectId=${selectedProject}` : '';
      const { data: res } = await api.get(`/billing/cost-overview${params}`);
      setData(res);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.hint || 'Failed to fetch billing data');
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Build chart data from projects list
  const chartData = data?.projects?.map((p, i) => ({
    name: (p.projectName || p.projectId || '').replace('projects/', '').substring(0, 18),
    projectId: p.projectId,
    billingEnabled: p.billingEnabled ? 1 : 0,
    color: SERVICE_COLORS[i % SERVICE_COLORS.length],
  })) || [];

  const billingEnabledProjects = data?.projects?.filter(p => p.billingEnabled) || [];
  const billingDisabledProjects = data?.projects?.filter(p => !p.billingEnabled) || [];

  const pieData = [
    { name: 'Billing Enabled', value: billingEnabledProjects.length, color: '#34A853' },
    { name: 'Billing Disabled', value: billingDisabledProjects.length, color: '#EA4335' },
  ].filter(d => d.value > 0);

  return (
    <div style={styles.layout}>
      <Sidebar />
      <main style={styles.main}>

        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.pageTitle}>Cost Overview</h1>
            <p style={styles.pageSubtitle}>
              Billing Account
              {data?.billingAccount?.id && (
                <code style={styles.accountId}> {data.billingAccount.id}</code>
              )}
            </p>
          </div>
          <div style={styles.headerActions}>
            {lastRefresh && (
              <span style={styles.refreshTime}>
                Updated {lastRefresh.toLocaleTimeString()}
              </span>
            )}
            <button style={styles.refreshBtn} onClick={fetchData} disabled={loading}>
              {loading ? '⟳ Loading...' : '⟳ Refresh'}
            </button>
          </div>
        </div>

        {/* Billing Account Info Bar */}
        {data?.billingAccount && (
          <div style={styles.accountBar}>
            <div style={styles.accountBarItem}>
              <span style={styles.accountBarLabel}>Billing Account</span>
              <span style={styles.accountBarValue}>{data.billingAccount.displayName || data.billingAccount.id}</span>
            </div>
            <div style={styles.accountBarDivider} />
            <div style={styles.accountBarItem}>
              <span style={styles.accountBarLabel}>Status</span>
              <StatusBadge enabled={data.billingAccount.open} />
            </div>
            <div style={styles.accountBarDivider} />
            <div style={styles.accountBarItem}>
              <span style={styles.accountBarLabel}>Currency</span>
              <span style={styles.accountBarValue}>{data.billingAccount.currencyCode || 'USD'}</span>
            </div>
            <div style={styles.accountBarDivider} />
            <div style={styles.accountBarItem}>
              <span style={styles.accountBarLabel}>Fetched</span>
              <span style={styles.accountBarValue}>{data.fetchedAt ? new Date(data.fetchedAt).toLocaleTimeString() : '—'}</span>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={styles.errorBox}>
            <div style={styles.errorTitle}>⚠ GCP API Error</div>
            <div style={styles.errorMsg}>{error}</div>
            <div style={styles.errorHint}>
              Make sure <code style={styles.code}>GCP_API_KEY</code> and <code style={styles.code}>GCP_BILLING_ACCOUNT_ID</code> are set in your <code style={styles.code}>backend/.env</code>.
              Also ensure the API key has <strong>Cloud Billing API</strong> and <strong>Cloud Resource Manager API</strong> enabled in GCP Console.
            </div>
          </div>
        )}

        {/* Loading skeleton */}
        {loading && !error && (
          <div style={styles.loadingGrid}>
            {[1, 2, 3, 4].map(i => (
              <div key={i} style={styles.skeleton} />
            ))}
          </div>
        )}

        {/* Stats */}
        {!loading && data && (
          <>
            <div style={styles.statsGrid}>
              <StatCard icon="📂" label="Total Projects" value={data.totalProjects || 0} sub="Linked to billing account" color="#4285F4" />
              <StatCard icon="✅" label="Billing Enabled" value={data.billingEnabledCount || 0} sub="Actively billed" color="#34A853" />
              <StatCard icon="🔴" label="Billing Disabled" value={(data.totalProjects || 0) - (data.billingEnabledCount || 0)} sub="No active billing" color="#EA4335" />
              <StatCard icon="🏦" label="Account Open" value={data.billingAccount?.open ? 'Yes' : 'No'} sub={data.billingAccount?.displayName} color="#FBBC05" />
            </div>

            {/* Project filter */}
            {data.projects?.length > 0 && (
              <div style={styles.filterRow}>
                <span style={styles.filterLabel}>Filter by project:</span>
                <select
                  value={selectedProject}
                  onChange={e => setSelectedProject(e.target.value)}
                  style={styles.select}
                >
                  <option value="">All Projects</option>
                  {data.projects.map(p => (
                    <option key={p.projectId} value={p.projectId}>
                      {p.projectName || p.projectId}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Charts row */}
            {data.projects?.length > 0 && (
              <div style={styles.chartsRow}>
                {/* Project billing status bar chart */}
                <div style={styles.chartCard}>
                  <div style={styles.chartHeader}>
                    <h3 style={styles.chartTitle}>Projects by Billing Status</h3>
                    <span style={styles.chartBadge}>{data.projects.length} projects</span>
                  </div>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={chartData} margin={{ left: -10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                      <XAxis dataKey="name" stroke="#475569" tick={{ fill: '#64748b', fontSize: 11 }} angle={-30} textAnchor="end" height={50} />
                      <YAxis stroke="#475569" tick={{ fill: '#64748b', fontSize: 11 }} ticks={[0, 1]} tickFormatter={v => v === 1 ? 'On' : 'Off'} />
                      <Tooltip
                        contentStyle={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, color: '#0f172a' }}
                        formatter={(v, n, props) => [props.payload.billingEnabled ? 'Billing Enabled' : 'Billing Disabled', props.payload.projectId]}
                      />
                      <Bar dataKey="billingEnabled" radius={[6, 6, 0, 0]}>
                        {chartData.map((entry, i) => (
                          <Cell key={i} fill={entry.billingEnabled ? '#34A853' : '#EA4335'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Pie chart */}
                <div style={styles.chartCard}>
                  <div style={styles.chartHeader}>
                    <h3 style={styles.chartTitle}>Billing Status Distribution</h3>
                  </div>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} strokeWidth={0}>
                        {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Pie>
                      <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, color: '#0f172a' }} />
                      <Legend formatter={v => <span style={{ color: '#94a3b8', fontSize: 13 }}>{v}</span>} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Projects table */}
            <div style={styles.tableCard}>
              <div style={styles.chartHeader}>
                <h3 style={styles.chartTitle}>All Projects</h3>
                <span style={styles.chartBadge}>{data.projects?.length || 0} total</span>
              </div>

              {!data.projects?.length ? (
                <div style={styles.emptyState}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
                  <div>No projects found linked to this billing account.</div>
                </div>
              ) : (
                <table style={styles.table}>
                  <thead>
                    <tr>
                      {['Project', 'Project ID', 'Billing Status', 'Lifecycle', 'Created', 'Labels'].map(h => (
                        <th key={h} style={styles.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.projects.map((p, i) => (
                      <tr key={i} style={styles.tr}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={styles.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ ...styles.projectDot, background: SERVICE_COLORS[i % SERVICE_COLORS.length] }} />
                            <span style={{ color: '#1e293b', fontWeight: 500 }}>
                              {p.projectName || p.projectId}
                            </span>
                          </div>
                        </td>
                        <td style={styles.td}>
                          <code style={styles.code}>{p.projectId}</code>
                        </td>
                        <td style={styles.td}>
                          <StatusBadge enabled={p.billingEnabled} />
                        </td>
                        <td style={styles.td}>
                          <span style={{ color: p.lifecycleState === 'ACTIVE' ? '#4ade80' : '#f87171', fontSize: 13 }}>
                            {p.lifecycleState || '—'}
                          </span>
                        </td>
                        <td style={styles.td}>
                          {p.createTime ? new Date(p.createTime).toLocaleDateString() : '—'}
                        </td>
                        <td style={styles.td}>
                          {p.labels && Object.keys(p.labels).length > 0
                            ? Object.entries(p.labels).slice(0, 2).map(([k, v]) => (
                              <span key={k} style={styles.label}>{k}: {v}</span>
                            ))
                            : <span style={{ color: '#94a3b8' }}>—</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* API note */}
            <div style={styles.noteCard}>
              <span style={{ fontSize: 16 }}>💡</span>
              <div>
                <div style={{ color: '#93c5fd', fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
                  Want per-project cost amounts?
                </div>
                <div style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.6 }}>
                  The Cloud Billing API shows project billing status. For detailed cost breakdown by service/SKU,
                  enable <strong style={{ color: '#94a3b8' }}>BigQuery Billing Export</strong> in GCP Console →
                  Billing → Billing Export. Once enabled, query cost data directly from BigQuery.
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

const styles = {
  layout: { display: 'flex', background: '#f5f7fa', minHeight: '100vh', fontFamily: "'Inter', 'Segoe UI', sans-serif" },
 main: { marginLeft:0, flex:1, padding:'12px 12px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  pageTitle: { margin: 0, color: '#0f172a', fontSize: 26, fontWeight: 700, letterSpacing: '-0.5px' },
  pageSubtitle: { margin: '4px 0 0', color: '#64748b', fontSize: 13 },
  accountId: { background: 'rgba(255,255,255,0.07)', padding: '2px 8px', borderRadius: 6, fontFamily: 'monospace', fontSize: 12, color: '#94a3b8' },
  headerActions: { display: 'flex', alignItems: 'center', gap: 14 },
  refreshTime: { color: '#94a3b8', fontSize: 12 },
  refreshBtn: { background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#94a3b8', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 13 },
  accountBar: { display: 'flex', alignItems: 'center', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 22px', marginBottom: 24, gap: 0 },
  accountBarItem: { display: 'flex', flexDirection: 'column', gap: 4, padding: '0 24px' },
  accountBarLabel: { color: '#94a3b8', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px' },
  accountBarValue: { color: '#1e293b', fontSize: 14, fontWeight: 600 },
  accountBarDivider: { width: 1, height: 32, background: 'rgba(255,255,255,0.07)' },
  errorBox: { background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 12, padding: '20px 24px', marginBottom: 24 },
  errorTitle: { color: '#f87171', fontWeight: 700, fontSize: 15, marginBottom: 6 },
  errorMsg: { color: '#fca5a5', fontSize: 14, marginBottom: 10, fontFamily: 'monospace' },
  errorHint: { color: '#94a3b8', fontSize: 13, lineHeight: 1.6 },
  loadingGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 },
  skeleton: { height: 110, borderRadius: 14, background: '#f1f5f9', animation: 'pulse 1.5s ease-in-out infinite' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 },
  statCard: { background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '20px 22px' },
  statIcon: { width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, marginBottom: 14 },
  statValue: { color: '#0f172a', fontSize: 26, fontWeight: 700, letterSpacing: '-0.5px', marginBottom: 4 },
  statLabel: { color: '#94a3b8', fontSize: 13, marginBottom: 2 },
  statSub: { color: '#94a3b8', fontSize: 11 },
  filterRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 },
  filterLabel: { color: '#94a3b8', fontSize: 13 },
  select: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 14px', color: '#1e293b', fontSize: 13, outline: 'none', cursor: 'pointer' },
  chartsRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 },
  chartCard: { background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '24px' },
  chartHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  chartTitle: { margin: 0, color: '#1e293b', fontSize: 16, fontWeight: 600 },
  chartBadge: { background: '#f1f5f9', color: '#94a3b8', fontSize: 11, padding: '4px 10px', borderRadius: 20 },
  tableCard: { background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '24px', marginBottom: 20 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', color: '#64748b', fontSize: 11, fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', padding: '10px 14px', borderBottom: '1px solid #f1f5f9' },
  td: { padding: '14px 14px', color: '#94a3b8', fontSize: 13, borderBottom: '1px solid #f8fafc', transition: 'background 0.1s' },
  tr: { transition: 'background 0.1s' },
  projectDot: { width: 10, height: 10, borderRadius: 3, flexShrink: 0 },
  code: { background: 'rgba(255,255,255,0.07)', padding: '2px 8px', borderRadius: 5, fontFamily: 'monospace', fontSize: 12, color: '#94a3b8' },
  label: { display: 'inline-block', background: 'rgba(66,133,244,0.1)', color: '#60a5fa', border: '1px solid rgba(66,133,244,0.15)', padding: '2px 8px', borderRadius: 6, fontSize: 11, marginRight: 4 },
  emptyState: { textAlign: 'center', padding: '40px', color: '#64748b' },
  noteCard: { display: 'flex', gap: 14, background: 'rgba(66,133,244,0.06)', border: '1px solid rgba(66,133,244,0.15)', borderRadius: 12, padding: '16px 20px' },
};